// Logs Generales — EXCLUSIVO del administrador global (system_admin).
//
// Centraliza la trazabilidad de TODOS los procesos de la plataforma en un único
// listado cronológico, filtrable por tipo de proceso Y por tenant (cuenta).
// Consume `GET /admin/logs` (UNION ALL en backend) con paginación.

import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi } from '../api/ApiContext.js';
import { Icon, PageHeader } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';
import { ActivityLogList, type ActivityLogPage } from './ActivityLogList.js';
import type { ActivityProcessType, AdminAuditCategory } from '../types.js';

export function GlobalLogsView() {
  const { t } = useTranslation();
  const api = useApi();
  // Cuentas para poblar el selector de tenant (filtro).
  const accounts = useAsync(() => api.admin.listAccounts(), []);
  const [tenantId, setTenantId] = useState('');

  const accountList = accounts.data?.items ?? [];
  // Opciones únicas de tenant con etiqueta "nombre del catálogo — email del
  // propietario", para saber de quién es cada cuenta. Se prioriza el owner
  // (sobre system_admin) al elegir el email representativo del tenant.
  const tenantOptions = useMemo(() => {
    const byTenant = new Map<string, { name: string; email: string; isOwner: boolean }>();
    for (const a of accountList) {
      const prev = byTenant.get(a.tenant_id);
      const isOwner = a.role === 'owner';
      // Guarda la primera; reemplaza solo si la nueva es owner y la previa no.
      if (!prev || (isOwner && !prev.isOwner)) {
        byTenant.set(a.tenant_id, { name: a.tenant_name ?? a.tenant_id, email: a.email, isOwner });
      }
    }
    return [...byTenant.entries()].map(([id, v]) => ({
      id,
      label: v.email ? `${v.name} — ${v.email}` : v.name,
    }));
  }, [accountList]);

  const fetchPage = useCallback(
    (params: {
      type: ActivityProcessType | '';
      category: AdminAuditCategory | '';
      limit: number;
      offset: number;
    }): Promise<ActivityLogPage> =>
      api.admin.listLogs({
        type: params.type || undefined,
        category: params.category || undefined,
        tenant_id: tenantId || undefined,
        limit: params.limit,
        offset: params.offset,
      }),
    [api, tenantId],
  );

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('globalLogs.pageTitle')}
        subtitle={t('globalLogs.pageSubtitle')}
        back
      />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        {/* Filtro por cuenta (tenant). */}
        <div className="field-slot">
          <Icon name="library_music" className="text-on-surface-variant text-[18px] mr-space-xs" />
          <select
            className="field-input"
            value={tenantId}
            onChange={(e) => setTenantId(e.target.value)}
            aria-label={t('globalLogs.filterByAccount')}
          >
            <option value="">{t('globalLogs.allAccounts')}</option>
            {tenantOptions.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
        </div>

        {/* `reloadKey = tenantId` fuerza recargar la lista al cambiar de cuenta. */}
        <ActivityLogList fetchPage={fetchPage} showTenant reloadKey={tenantId} />
      </div>
    </div>
  );
}
